import React, { useEffect, useState, useCallback } from 'react'
import { CircularProgress, Tabs, Tab, Link, Grid, Typography, Tooltip, useTheme } from '@material-ui/core'
import NotInterestedIcon from '@material-ui/icons/NotInterested'
import moment from 'moment'
import localization from 'moment/locale/pt-br'

import { Page, Header, CustomTable } from 'components'
import { useVestigioService } from 'hooks/useVestigioService'
import { useSolicitacaoVestigioService } from 'hooks/useSolicitacaoVestigioService'
import { useCestaOperacaoLote } from 'hooks/useCestaOperacaoLote'
import { useAuth } from 'hooks/useAuth'
import { getLabelStatusSolicitacaoVestigio } from 'helpers/labels'
import useRouter from 'utils/useRouter'
import { useStyles } from './MeusVestigios.styles'

const sortSolicitacao = (a, b) => {
  if (moment(a.data).isSame(b.data)) {
    return a.createdAt < b.createdAt ? -1 : 1
  }
  return a.data < b.data ? -1 : 1
}

export const MeusVestigios = (props) => {
  const { route } = props
  const { socket } = useAuth()
  const vestigioService = useVestigioService()
  const solicitacaoVestigioService = useSolicitacaoVestigioService()
  const classes = useStyles()
  const theme = useTheme()
  const { addVestigio, verificarItemEmCesta } = useCestaOperacaoLote()
  const router = useRouter()
  moment.locale('pt-br', localization)

  const [tabValue, setTabValue] = useState(0)
  const [meusVestigios, setMeusVestigios] = useState([])
  const [meusVestigiosEncaminhados, setVestigiosEncaminhados] = useState([])
  const [solicitacoesVestigio, setSolicitacoesVestigio] = useState([])
  const [processandoVestigios, setProcessandoVestigios] = useState(true)

  const fetchMeusVestigios = useCallback(async () => {
    try {
      setProcessandoVestigios(true)
      const responseDoUsuario = await vestigioService.listarVestigiosDoUsuarioAsync()
      setMeusVestigios(responseDoUsuario || [])
      const responseEncaminhado = await vestigioService.listarVestigiosEncaminhadosParaUsuarioAsync()
      setVestigiosEncaminhados(responseEncaminhado || [])
    } catch (error) {
      console.error('Erro ao buscar meus vestígios:', error)
    } finally {
      setProcessandoVestigios(false)
    }
  }, [])

  const fetchSolicitacoesVestigio = useCallback(async () => {
    try {
      const responseSolicitacoes = await solicitacaoVestigioService.listarSolicitacoesVestigioDeUsuario()
      setSolicitacoesVestigio(responseSolicitacoes || [])
    } catch (error) {
      console.error('Erro ao buscar solicitações de vestígios:', error)
    }
  }, [])

  useEffect(() => {
    fetchMeusVestigios()
    fetchSolicitacoesVestigio()
  }, [fetchMeusVestigios, fetchSolicitacoesVestigio])

  useEffect(() => {
    if (socket) {
      socket.on('atualizarSolicitacoes', () => {
        fetchSolicitacoesVestigio()
      })
    }
  }, [socket, fetchSolicitacoesVestigio])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleRowClick = (rowData) => {
    router.history.push(`/casos/caso/vestigio/detalhar/${rowData.id}`)
  }

  const redirectLote = useCallback(
    (id) => {
      router.history.push(`/vestigios/lote/${id}`)
    },
    [router.history]
  )

  const criarLote = useCallback(
    async (id) => {
      try {
        setProcessandoVestigios(true)
        const response = await vestigioService.criarLote(id)
        router.history.push(`/vestigios/meusvestigios/lote/${response.id}`)
      } catch (error) {
        console.error('Erro ao criar lote:', error)
      } finally {
        setProcessandoVestigios(false)
      }
    },
    [router.history]
  )

  const handleCancelarSolicitacao = async (event, rowData) => {
    try {
      await solicitacaoVestigioService.alterarStatusSolicitacao(rowData.id, 'CANCELADA', setProcessandoVestigios)
      fetchSolicitacoesVestigio()
    } catch (error) {
      console.error('Erro ao cancelar solicitação:', error)
    }
  }

  const colunasCustodia = [
    {
      title: 'Identificador',
      field: 'identificador',
      cellStyle: { fontWeight: 600 },
      customSort: (a, b) => a.identificador.localeCompare(b.identificador),
    },
    {
      title: 'Lacre',
      render: (rowData) => rowData.lacre || '-',
      customSort: (a, b) => a.lacre?.localeCompare(b.lacre),
    },
    {
      title: 'Lote',
      width: '10%',
      render: (rowData) =>
        rowData.lote ? (
          <Link
            onClick={(e) => {
              e.stopPropagation()
              redirectLote(rowData.lote.id)
            }}
            style={{ cursor: 'pointer' }}
          >
            {rowData.lote.numero}
          </Link>
        ) : (
          <Link
            onClick={(e) => {
              e.stopPropagation()
              criarLote(rowData.id)
            }}
            style={{ cursor: 'pointer' }}
          >
            Criar lote
          </Link>
        ),
      customSort: (a, b) => a.lote?.numero.localeCompare(b.lote?.numero),
    },
    {
      title: 'Natureza',
      cellStyle: { minWidth: '250px' },
      render: (rowData) => {
        const gen = rowData.tiposGens?.map((t) => t.nome?.toUpperCase()).join(', ')
        const esp = rowData.tiposEsps?.map((t) => t.nome?.toUpperCase()).join(', ')
        if (gen && esp) return `${gen} / ${esp}`
        return gen || esp || '-'
      },
      customSort: (a, b) =>
        a.tiposGens
          ?.map((t) => t.nome?.toUpperCase())
          .join(', ')
          .localeCompare(b.tiposGens?.map((t) => t.nome?.toUpperCase()).join(', ')),
    },
    {
      title: 'Descrição',
      field: 'descricao',
      cellStyle: { minWidth: '250px' },
      render: (rowData) => (
        <Tooltip title={rowData.descricao?.toUpperCase() || 'SEM DESCRIÇÃO'} arrow placement="top-start">
          <div
            style={{
              maxWidth: '380px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: theme.palette.text.secondary,
            }}
          >
            {rowData.descricao?.toUpperCase() || 'SEM DESCRIÇÃO REGISTRADA'}
          </div>
        </Tooltip>
      ),
      customSort: (a, b) => a.descricao.localeCompare(b.descricao),
    },
    {
      title: 'Setor atual',
      render: (rowData) => (
        <Tooltip title={rowData.setAtual ? `${rowData.setAtual.nome.toUpperCase()} / ${rowData.setAtual.divisao.sigla.toUpperCase()} / ${rowData.setAtual.divisao.regional.sigla.toUpperCase()}` : '-'} arrow placement="top-start">
          <div
            style={{
              maxWidth: '380px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: theme.palette.text.secondary,
            }}
          >
            {rowData.setAtual ? `${rowData.setAtual.nome.toUpperCase()} / ${rowData.setAtual.divisao.sigla.toUpperCase()} / ${rowData.setAtual.divisao.regional.sigla.toUpperCase()}` : '-'}</div>
        </Tooltip>),
    },
    {
      title: 'Data',
      render: (rowData) => (rowData.createdAt ? moment(rowData.createdAt).format('DD/MM/YYYY HH:mm') : '-'),
      customSort: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
  ]

  const colunasSolicitados = [
    {
      title: 'Vestígios',
      width: '5%',
      render: (rowData) => rowData.vestigios?.length || '-',
      customSort: (a, b) => a.vestigios?.length.localeCompare(b.vestigios?.length),
    },
    {
      title: 'Horário de Solicitação',
      field: 'createdAt',
      render: (rowData) => (rowData.createdAt ? moment(rowData.createdAt).fromNow() : '-'),
      customSort: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
    {
      title: 'Horário para Atendimento',
      field: 'data',
      render: (rowData) => (rowData.data ? moment(rowData.data).calendar() : '-'),
      customSort: (a, b) => a.data.localeCompare(b.data),
    },
    {
      title: 'Status',
      field: 'status',
      render: (rowData) => getLabelStatusSolicitacaoVestigio(rowData.status),
      customSort: (a, b) => a.status.localeCompare(b.status),
    },
  ]

  const acoesCesta = [
    (rowData) => ({
      icon: () => <img src="/images/cesta-add-black.png" alt="Adicionar à cesta" className="img-icon-invert-dark" style={{ width: 24, height: 24 }} />,
      tooltip: 'Adicionar à cesta',
      onClick: (event, rowData) => addVestigio(rowData),
      hidden: verificarItemEmCesta(rowData.id),
    }),
  ]

  const acoesSolicitados = [
    (rowData) => ({
      icon: () => <NotInterestedIcon />,
      tooltip: 'Cancelar solicitação',
      onClick: handleCancelarSolicitacao,
      disabled: rowData.status !== 'PENDENTE',
    }),
  ]

  return (
    <Page className={classes.root} title={`${route.secao} - SIGEP`}>
      <Header titulo={`${route.secao}`} overline={route.secao} />

      <div className={classes.tabsContainer}>
        <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" aria-label="Abas de Filtros de Meus Vestígios">
          <Tab label="Em minha custódia" />
          <Tab label="Encaminhados para mim" />
          <Tab label="Solicitados por mim" />
        </Tabs>
      </div>

      {processandoVestigios ? (
        <div className={classes.carregando}>
          <CircularProgress disableShrink />
        </div>
      ) : tabValue === 0 ? (
        <CustomTable columns={colunasCustodia} data={meusVestigios} actions={acoesCesta} onRowClick={(event, rowData) => handleRowClick(rowData)} searchPlaceholder="Buscar vestígios..." emptyDataSourceMessage="Nenhum vestígio em minha custódia encontrado" />
      ) : tabValue === 1 ? (
        <CustomTable columns={colunasCustodia} data={meusVestigiosEncaminhados} actions={acoesCesta} onRowClick={(event, rowData) => handleRowClick(rowData)} searchPlaceholder="Buscar vestígios..." emptyDataSourceMessage="Nenhum vestígio encaminhado para mim encontrado" />
      ) : (
        <CustomTable columns={colunasSolicitados} data={solicitacoesVestigio} actions={acoesSolicitados} searchPlaceholder="Buscar solicitações..." emptyDataSourceMessage="Nenhuma solicitação de vestígio encontrada" />
      )}
    </Page>
  )
}
